/** Persistent workspace preferences (per browser). */
import { useCallback, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export function usePref<T>(key: string, initial: T): [T, (v: T) => void] {
  const storageKey = `prism.pref.${key}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (v: T) => {
      setValue(v);
      try {
        localStorage.setItem(storageKey, JSON.stringify(v));
      } catch {
        /* storage may be unavailable (private mode); preference stays in memory */
      }
    },
    [storageKey],
  );
  return [value, set];
}

/** localStorage that never throws (private mode, blocked or full storage): reads give null, writes are dropped. */
export const safeStorage = {
  getItem: (k: string) => {
    try { return localStorage.getItem(k); } catch { return null; }
  },
  setItem: (k: string, v: string) => {
    try { localStorage.setItem(k, v); } catch { /* ignore */ }
  },
  removeItem: (k: string) => {
    try { localStorage.removeItem(k); } catch { /* ignore */ }
  },
};

/** Where resizable pane groups remember their sizes. */
export const layoutStorage = safeStorage;

/**
 * Writes of server preferences, one at a time per key (0.96.0). Two quick changes used to be two PUTs in flight at
 * once; the server could finish the older one last and keep it (the Library's remembered filters lost a filter that
 * way, mostly under load). Now a change made while a write is in flight waits for it and only the latest is sent.
 * Writes use keepalive, so one under way when the page is left still arrives; a waiting one is sent at pagehide.
 */
const prefWrites = new Map<string, { inflight: boolean; pending: { value: unknown } | null }>();
const putPref = (key: string, value: unknown) => api(`/api/v1/me/preferences/${key}`, { method: 'PUT', json: { value }, keepalive: true });

export function writeServerPref(key: string, value: unknown): Promise<void> {
  let w = prefWrites.get(key);
  if (!w) prefWrites.set(key, (w = { inflight: false, pending: null }));
  const slot = w;
  if (slot.inflight) { slot.pending = { value }; return Promise.resolve(); }
  slot.inflight = true;
  return putPref(key, value).then(() => undefined, () => undefined).then(() => {
    slot.inflight = false;
    const next = slot.pending;
    slot.pending = null;
    if (next) return writeServerPref(key, next.value);
    return undefined;
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    for (const [key, w] of prefWrites) {
      if (!w.pending) continue;
      const { value } = w.pending;
      w.pending = null;
      void putPref(key, value).catch(() => undefined);
    }
  });
}

/**
 * A preference kept on the server for the signed-in person (it follows them
 * between browsers), cached in this browser so the page draws at once.
 */
export function useServerPref<T>(key: string, initial: T): [T, (v: T | null) => void, boolean] {
  const qc = useQueryClient();
  const cacheKey = `prism.spref.${key}`;
  const q = useQuery({
    queryKey: ['pref', key],
    queryFn: async ({ signal }) => {
      const r = await api<{ value: T | null }>(`/api/v1/me/preferences/${key}`);
      // Cancelled by a change made meanwhile (below): the answer is older than that change.
      if (!signal.aborted) try { localStorage.setItem(cacheKey, JSON.stringify(r.value)); } catch { /* ignore */ }
      return r.value;
    },
    staleTime: 5 * 60_000,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    placeholderData: (() => {
      try { const raw = localStorage.getItem(cacheKey); return raw ? JSON.parse(raw) : undefined; } catch { return undefined; }
    }) as any,
  });
  const set = useCallback((v: T | null) => setServerPref(qc, key, v), [qc, key]);
  return [(q.data ?? initial) as T, set, q.data != null];
}

/**
 * A change to a server preference: shown at once, cached in this browser and written to the server. A change made
 * while the preference's GET is still in flight (1.2.0) cancels that GET first, so its older answer cannot overwrite
 * the change when it arrives (the Panes menu's checkboxes were undone that way). The cancel reverts the query
 * synchronously, so the value set next is the one that stays.
 */
export function setServerPref(qc: QueryClient, key: string, v: unknown) {
  void qc.cancelQueries({ queryKey: ['pref', key], exact: true });
  qc.setQueryData(['pref', key], v);
  try { localStorage.setItem(`prism.spref.${key}`, JSON.stringify(v)); } catch { /* ignore */ }
  void writeServerPref(key, v);
}
